import { useCallback, useRef, useState } from "react";

const STORAGE_KEY = "senseguard:editor-master-width";
const FALLBACK_MIN_WIDTH = 260;
const MAX_WIDTH = 560;
const DEFAULT_WIDTH = 340;

function readStoredWidth(): number {
  if (typeof window === "undefined") return DEFAULT_WIDTH;
  const raw = Number(window.localStorage.getItem(STORAGE_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_WIDTH;
}

/**
 * Persisted, drag-resizable master panel width (px), shared across master/detail editors
 * via localStorage. Attach `tabsRef` to the tabs row so the panel can never be resized
 * narrower than the space its tab labels/counts need (which varies by locale and data).
 */
export function useResizablePanelWidth() {
  const minWidthRef = useRef(FALLBACK_MIN_WIDTH);
  const [width, setWidth] = useState(() =>
    Math.min(MAX_WIDTH, Math.max(FALLBACK_MIN_WIDTH, readStoredWidth())),
  );
  const [minWidth, setMinWidth] = useState(FALLBACK_MIN_WIDTH);
  const dragState = useRef<{ startX: number; startWidth: number } | null>(null);

  const applyMinWidth = useCallback((next: number) => {
    minWidthRef.current = next;
    setMinWidth(next);
    setWidth((w) => Math.min(MAX_WIDTH, Math.max(next, w)));
  }, []);

  const tabsRef = useCallback(
    (el: HTMLElement | null) => {
      if (!el || typeof ResizeObserver === "undefined") return;
      // Measure the intrinsic content width (children + gaps + padding), NOT el.scrollWidth:
      // the tabs row is `w-full`, so its scrollWidth tracks the panel width and would ratchet
      // the min-width up on every resize, blocking the drag from shrinking the panel again.
      const measure = () => {
        const style = getComputedStyle(el);
        const gap = parseFloat(style.columnGap) || 0;
        const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        const children = Array.from(el.children) as HTMLElement[];
        const content =
          children.reduce((sum, child) => sum + child.getBoundingClientRect().width, 0) +
          gap * Math.max(0, children.length - 1) +
          padX;
        applyMinWidth(Math.max(FALLBACK_MIN_WIDTH, Math.ceil(content)));
      };
      measure();
      // Observe the individual triggers (their natural width changes with locale/counts),
      // not the stretched container (whose box only reflects the panel width).
      const observer = new ResizeObserver(measure);
      for (const child of Array.from(el.children)) observer.observe(child);
      return () => observer.disconnect();
    },
    [applyMinWidth],
  );

  const handlePointerMove = useCallback((e: PointerEvent) => {
    const drag = dragState.current;
    if (!drag) return;
    const next = Math.min(
      MAX_WIDTH,
      Math.max(minWidthRef.current, drag.startWidth + (e.clientX - drag.startX)),
    );
    setWidth(next);
  }, []);

  const handlePointerUp = useCallback(() => {
    dragState.current = null;
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    setWidth((w) => {
      window.localStorage.setItem(STORAGE_KEY, String(w));
      return w;
    });
  }, [handlePointerMove]);

  const startResize = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragState.current = { startX: e.clientX, startWidth: width };
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [width, handlePointerMove, handlePointerUp],
  );

  // Keyboard resizing (arrow keys on the separator): same clamp and same storage as the drag.
  const setWidthTo = useCallback((next: number) => {
    const clamped = Math.min(MAX_WIDTH, Math.max(minWidthRef.current, Math.round(next)));
    window.localStorage.setItem(STORAGE_KEY, String(clamped));
    setWidth(clamped);
  }, []);

  // Spread on <ResizeHandle> next to onPointerDown to give the separator its keyboard support.
  const resizeProps = { value: width, min: minWidth, max: MAX_WIDTH, onValueChange: setWidthTo };

  return { width, startResize, tabsRef, resizeProps };
}
